import type { GeneratedFile } from '@cli-types/core-engine/schema.js'
import type {
  ApplyGeneratedFilesOptions,
  ApplyGeneratedFilesSummary
} from '@cli-types/next-adapter/snapshots.js'
import { getCurrentCliVersion } from '@cli-utils/paths.js'
import { isInteractiveSession } from '@cli-utils/terminal.js'
import { hasConflictMarkers, normalizeLineEndings } from '@cli-utils/text.js'
import { buildRenamePlanAliasMap } from '@core-engine/snapshots/ast-substitution/build-rename-plan-alias-map.js'
import { transformContentForRenamePlan } from '@core-engine/snapshots/ast-substitution/transform-content-for-rename-plan.js'
import { deleteProjectFile } from '@core-engine/snapshots/merge/delete-project-file.js'
import { mergeGeneratedFile } from '@core-engine/snapshots/merge/merge-generated-file.js'
import { readProjectFile } from '@core-engine/snapshots/merge/read-project-file.js'
import { writeProjectFile } from '@core-engine/snapshots/merge/write-project-file.js'
import { createManifest } from '@core-engine/snapshots/store/create-manifest.js'
import { loadManifest } from '@core-engine/snapshots/store/load-manifest.js'
import { saveManifest } from '@core-engine/snapshots/store/save-manifest.js'
import { writeSnapshotFiles } from '@core-engine/snapshots/store/write-snapshot-files.js'
import { reconstructSnapshotFiles } from '@next-adapter/snapshots/reconstruct/reconstruct-snapshot-files.js'
import { buildMergeInput } from './build-merge-input.js'
import { promptNoBaseDecision } from './prompt-no-base-decision.js'
import { writeBackupFile } from './write-backup-file.js'

export async function applyGeneratedFiles({
  cwd,
  config,
  scope,
  schemaJson,
  generatedFiles,
  force,
  acceptGenerated = false,
  interactive,
  renamePlan
}: ApplyGeneratedFilesOptions): Promise<ApplyGeneratedFilesSummary> {
  const manifest = loadManifest(cwd, scope)
  const summary: ApplyGeneratedFilesSummary = {
    writtenFresh: [],
    overwritten: [],
    mergedCleanly: [],
    acceptedGenerated: [],
    conflictPaths: [],
    orphanedKept: [],
    orphanedDeleted: [],
    skipped: [],
    skippedCleared: []
  }

  const remoteFiles = [...generatedFiles].sort((a, b) => a.path.localeCompare(b.path))
  const remotePaths = new Set(remoteFiles.map((file) => file.path))
  const skipped = new Set(force ? [] : (manifest?.skipped ?? []))
  const interactiveSession = interactive && isInteractiveSession()
  const aliasMap = renamePlan ? buildRenamePlanAliasMap(renamePlan) : new Map<string, string>()
  const consumedBasePaths = new Set<string>()

  let baseFiles = new Map<string, GeneratedFile>()
  let mismatchedPaths = new Set<string>()
  let trackedBasePaths = new Set<string>()
  if (manifest) {
    const reconstructed = reconstructSnapshotFiles(cwd, config, manifest)
    baseFiles = new Map(reconstructed.files.map((file) => [file.path, file]))
    mismatchedPaths = new Set(reconstructed.mismatchedPaths)
    trackedBasePaths = new Set(reconstructed.trackedPaths)
  }

  for (const file of remoteFiles) {
    if (skipped.has(file.path) && !force) {
      continue
    }

    if (force) {
      const localContent = readProjectFile(cwd, file.path)
      writeProjectFile(cwd, file)
      if (localContent == null) {
        summary.writtenFresh.push(file.path)
      } else if (normalizeLineEndings(localContent) !== normalizeLineEndings(file.content)) {
        summary.overwritten.push(file.path)
      }
      continue
    }

    const aliasPath = aliasMap.get(file.path)
    let baseFile = baseFiles.get(file.path)
    let localContent = readProjectFile(cwd, file.path)
    let aliasedLocalPath: string | null = null

    if (!baseFile && aliasPath && !mismatchedPaths.has(aliasPath)) {
      const aliasedBase = baseFiles.get(aliasPath)
      if (aliasedBase) {
        const transformedBase = renamePlan
          ? transformContentForRenamePlan(aliasedBase.content, aliasPath, renamePlan, file.path)
          : null

        if (!transformedBase?.parseFailed) {
          baseFile = {
            path: file.path,
            content: transformedBase ? transformedBase.content : aliasedBase.content
          }
          consumedBasePaths.add(aliasPath)
        }
      }
    }

    if (baseFile && localContent == null && aliasPath) {
      const aliasedLocal = readProjectFile(cwd, aliasPath)
      if (aliasedLocal != null) {
        const transformedLocal = renamePlan
          ? transformContentForRenamePlan(aliasedLocal, aliasPath, renamePlan, file.path)
          : null

        if (!transformedLocal?.parseFailed) {
          localContent = transformedLocal ? transformedLocal.content : aliasedLocal
          aliasedLocalPath = aliasPath
        }
      }
    }

    if (baseFile && renamePlan && baseFile.path === file.path) {
      const transformedBase = transformContentForRenamePlan(baseFile.content, file.path, renamePlan)
      if (!transformedBase.parseFailed) {
        baseFile = {
          path: file.path,
          content: transformedBase.content
        }
      }
    }

    if (localContent != null && renamePlan && !aliasedLocalPath) {
      const transformedLocal = transformContentForRenamePlan(localContent, file.path, renamePlan)
      if (!transformedLocal.parseFailed) {
        localContent = transformedLocal.content
      }
    }

    if (localContent == null) {
      writeProjectFile(cwd, file)
      summary.writtenFresh.push(file.path)
      continue
    }

    if (!baseFile || mismatchedPaths.has(file.path)) {
      const decision = force
        ? 'overwrite'
        : interactiveSession
          ? await promptNoBaseDecision(file.path)
          : 'backup'

      if (decision === 'skip') {
        skipped.add(file.path)
        summary.skipped.push(file.path)
        continue
      }

      if (decision === 'backup') {
        writeBackupFile(cwd, file.path, localContent)
      }

      writeProjectFile(cwd, file)
      if (aliasedLocalPath) {
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      summary.writtenFresh.push(file.path)
      continue
    }

    if (normalizeLineEndings(localContent) === normalizeLineEndings(file.content)) {
      if (aliasedLocalPath) {
        writeProjectFile(cwd, file)
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      continue
    }

    if (acceptGenerated && hasConflictMarkers(localContent)) {
      writeProjectFile(cwd, file)
      if (aliasedLocalPath) {
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      summary.acceptedGenerated.push(file.path)
      continue
    }

    const mergeInput = buildMergeInput(cwd, baseFile, localContent, file)

    if (
      normalizeLineEndings(mergeInput.localContent) ===
      normalizeLineEndings(mergeInput.remoteFile.content)
    ) {
      if (aliasedLocalPath) {
        writeProjectFile(cwd, mergeInput.remoteFile)
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      continue
    }

    if (
      normalizeLineEndings(mergeInput.localContent) ===
      normalizeLineEndings(mergeInput.baseFile.content)
    ) {
      writeProjectFile(cwd, mergeInput.remoteFile)
      if (aliasedLocalPath) {
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      summary.mergedCleanly.push(file.path)
      continue
    }

    if (
      normalizeLineEndings(mergeInput.remoteFile.content) ===
      normalizeLineEndings(mergeInput.baseFile.content)
    ) {
      if (aliasedLocalPath) {
        writeProjectFile(cwd, {
          path: file.path,
          content: mergeInput.localContent
        })
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      continue
    }

    const merged = mergeGeneratedFile(
      mergeInput.baseFile,
      mergeInput.localContent,
      mergeInput.remoteFile
    )
    writeProjectFile(cwd, merged.conflict && acceptGenerated ? mergeInput.remoteFile : merged.file)
    if (aliasedLocalPath) {
      deleteProjectFile(cwd, aliasedLocalPath)
    }
    if (merged.conflict) {
      if (acceptGenerated) {
        summary.acceptedGenerated.push(file.path)
      } else {
        summary.conflictPaths.push(file.path)
      }
    } else {
      summary.mergedCleanly.push(file.path)
    }
  }

  const orphanCandidatePaths = Array.from(
    new Set([...trackedBasePaths, ...baseFiles.keys()])
  ).sort()

  for (const filePath of orphanCandidatePaths) {
    if (consumedBasePaths.has(filePath)) {
      continue
    }

    if (remotePaths.has(filePath)) {
      continue
    }

    const localContent = readProjectFile(cwd, filePath)
    if (localContent == null) {
      continue
    }

    if (force) {
      deleteProjectFile(cwd, filePath)
      summary.orphanedDeleted.push(filePath)
      continue
    }

    const baseFile = baseFiles.get(filePath)
    if (!baseFile) {
      continue
    }

    if (normalizeLineEndings(localContent) === normalizeLineEndings(baseFile.content)) {
      deleteProjectFile(cwd, filePath)
      summary.orphanedDeleted.push(filePath)
    } else {
      summary.orphanedKept.push(filePath)
    }
  }

  const nextSkipped = Array.from(skipped)
    .filter((filePath) => remotePaths.has(filePath))
    .sort()
  summary.skippedCleared = Array.from(skipped)
    .filter((filePath) => !remotePaths.has(filePath))
    .sort()

  const trackedFiles = remoteFiles.filter((file) => !nextSkipped.includes(file.path))
  writeSnapshotFiles(cwd, scope, trackedFiles)
  const nextManifest = createManifest(
    scope,
    schemaJson,
    getCurrentCliVersion(),
    trackedFiles,
    nextSkipped
  )
  saveManifest(cwd, scope, nextManifest)

  return summary
}
